/**
 * Copilot audit log — every accepted proposal lands here so the user can
 * see what the copilot did and undo any of it.
 *
 * Phase 0 stores up to 100 entries in localStorage. Phase 1 will sync
 * to `/v1/platform/audit-log` for tenant-wide history + GDPR export.
 *
 * Each entry captures:
 *   - `toolId` — which tool ran (matches a key in `COPILOT_TOOLS`)
 *   - `payload` — the apply payload (so we can re-render the proposal in history)
 *   - `undoData` — opaque blob from the tool's `apply()` that reverses the action
 *   - `status` — "applied" | "undone" (we keep undone entries for visibility)
 *   - `proposalId` — links back to the proposal card in the chat
 *   - `summary` — short human-readable label ("Added gauge to Plant Overview")
 */

import { useEffect, useState } from "react";

export interface AuditEntry {
  id: string;
  t: string; // ISO timestamp
  toolId: string;
  payload: unknown;
  undoData: unknown;
  status: "applied" | "undone";
  proposalId?: string;
  summary: string;
}

const STORAGE_KEY = "divineiot:copilot-audit";
const MAX_ENTRIES = 100;

type Listener = () => void;
const listeners = new Set<Listener>();
let cachedEntries: AuditEntry[] | null = null;

function read(): AuditEntry[] {
  if (cachedEntries) return cachedEntries;
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cachedEntries = [];
      return cachedEntries;
    }
    const parsed = JSON.parse(raw);
    cachedEntries = Array.isArray(parsed) ? (parsed as AuditEntry[]) : [];
    return cachedEntries;
  } catch {
    cachedEntries = [];
    return cachedEntries;
  }
}

function write(entries: AuditEntry[]) {
  cachedEntries = entries;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* swallow */
  }
  for (const fn of listeners) fn();
}

/** Append a new audit entry. Caps the log at MAX_ENTRIES (oldest dropped). */
export function recordAudit(
  toolId: string,
  payload: unknown,
  undoData: unknown,
  summary: string,
  proposalId?: string,
): AuditEntry {
  const entry: AuditEntry = {
    id: `aud-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
    t: new Date().toISOString(),
    toolId,
    payload,
    undoData,
    status: "applied",
    proposalId,
    summary,
  };
  const next = [entry, ...read()].slice(0, MAX_ENTRIES);
  write(next);
  return entry;
}

/** Mark an entry as undone — leaves it in the log so the user can see history. */
export function markUndone(entryId: string) {
  const next = read().map((e) =>
    e.id === entryId ? { ...e, status: "undone" as const } : e,
  );
  write(next);
}

/** Lookup a single entry by id. */
export function getAuditEntry(entryId: string): AuditEntry | undefined {
  return read().find((e) => e.id === entryId);
}

/** Clear the entire log (used by the "Reset session" admin control). */
export function clearAudit() {
  write([]);
}

/**
 * React hook — subscribes to audit-log changes so a `useCopilotAudit()`
 * consumer re-renders whenever a new entry is recorded or undone.
 */
export function useCopilotAudit(): AuditEntry[] {
  const [entries, setEntries] = useState<AuditEntry[]>(() => read());
  useEffect(() => {
    const listener: Listener = () => setEntries([...read()]);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return entries;
}
